import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  useTheme,
  useMediaQuery,
  Paper,
  Avatar,
  alpha,
  Fade,
} from '@mui/material';
import {
  SentimentSatisfied,
  Tag,
  EmojiEmotions,
  SmartToy,
  ReadMore,
  Psychology,
  Summarize,
  Label,
  CompareArrows,
  ArrowForward,
  TrendingUp,
  Speed,
  Security,
  AutoAwesome,
  CheckCircle,
  Rocket,
  RocketLaunch,
  CleaningServices,
  Insights,
  FormatQuote,
  Quiz,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTheme as useCustomTheme } from '../theme/ThemeProvider';

const tools = [
  {
    title: 'Sentiment Analyzer',
    description: 'Analyze the emotional tone of your text with advanced AI-powered sentiment detection.',
    icon: <SentimentSatisfied sx={{ fontSize: 40 }} />,
    path: '/SentimentAnalyzer',
    color: '#6366f1',
    gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
  },
  {
    title: 'Hashtag Generator',
    description: 'Generate optimized hashtags to maximize your social media reach and engagement.',
    icon: <Tag sx={{ fontSize: 40 }} />,
    path: '/HashtagsAnalyzer',
    color: '#06b6d4',
    gradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
  },
  {
    title: 'Emoji Suggester',
    description: 'Transform your text with smart emoji suggestions to make it more engaging.',
    icon: <EmojiEmotions sx={{ fontSize: 40 }} />,
    path: '/EmojisAnalyzer',
    color: '#f59e0b',
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
  },
  {
    title: 'AI Text Detector',
    description: 'Identify AI-generated content with high accuracy using advanced detection.',
    icon: <SmartToy sx={{ fontSize: 40 }} />,
    path: '/AITextAnalyzer',
    color: '#ef4444',
    gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
  },
  {
    title: 'Readability Checker',
    description: 'Evaluate and improve the readability of your content for better engagement.',
    icon: <ReadMore sx={{ fontSize: 40 }} />,
    path: '/ReadabilityAnalyzer',
    color: '#10b981',
    gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  },
  {
    title: 'Text Summarizer',
    description: 'Transform long texts into concise summaries using advanced AI technology.',
    icon: <Summarize sx={{ fontSize: 40 }} />,
    path: '/TextSummarizationAnalyzer',
    color: '#f97316',
    gradient: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
  },
  {
    title: 'Keyword Extractor',
    description: 'Extract meaningful keywords and keyphrases from your text using AI.',
    icon: <Label sx={{ fontSize: 40 }} />,
    path: '/KeywordExtractionAnalyzer',
    color: '#22c55e',
    gradient: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
  },
  {
    title: 'Semantic Similarity',
    description: 'Analyze semantic relationships between texts using advanced AI embeddings.',
    icon: <CompareArrows sx={{ fontSize: 40 }} />,
    path: '/SemanticSimilarityAnalyzer',
    color: '#ec4899',
    gradient: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
  },
  {
    title: 'Post Splitter',
    description: 'Break down long content into optimal chunks for social media platforms.',
    icon: <Psychology sx={{ fontSize: 40 }} />,
    path: '/PostSplitter',
    color: '#8b5cf6',
    gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
  },
  {
    title: 'AI Content Humanizer',
    description: 'Transform robotic AI text into natural, human-like content that passes detectors and engages readers.',
    icon: <AutoAwesome sx={{ fontSize: 40 }} />,
    path: '/HumanizerAnalyzer',
    color: '#f59e0b',
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
  },
  {
    title: '3-Minute Creator',
    description: 'Transform a single piece of content into a complete social media campaign in seconds.',
    icon: <RocketLaunch sx={{ fontSize: 40 }} />,
    path: '/RepurposerAnalyzer',
    color: '#6366f1',
    gradient: 'linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)',
  },
  {
    title: 'Smart Simplifier',
    description: 'Micro-edit your text sentence-by-sentence to achieve maximum clarity and readability.',
    icon: <CleaningServices sx={{ fontSize: 40 }} />,
    path: '/SimplifierAnalyzer',
    color: '#10b981',
    gradient: 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)',
  },
  {
    title: 'Semantic SEO Analyzer',
    description: 'Optimize your content for search engines by identifying missing semantic entities and relevant sub-topics.',
    icon: <Insights sx={{ fontSize: 40 }} />,
    path: '/SEOAnalyzer',
    color: '#4f46e5',
    gradient: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
  },
  {
    title: 'Smart Quote Cards',
    description: 'Instantly turn your text into beautiful, shareable quote cards for social media.',
    icon: <FormatQuote sx={{ fontSize: 40 }} />,
    path: '/QuoteAnalyzer',
    color: '#ec4899',
    gradient: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
  },
  {
    title: 'Flashcards & Quiz',
    description: 'Turn any lesson or article into interactive flashcards and study aids instantly.',
    icon: <Quiz sx={{ fontSize: 40 }} />,
    path: '/FlashcardAnalyzer',
    color: '#3b82f6',
    gradient: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
  },
];

const features = [
  {
    icon: <Rocket sx={{ fontSize: 32 }} />,
    title: 'Instant Results',
    description: 'Get powerful insights in seconds with our lightning-fast AI processing.',
  },
  {
    icon: <Security sx={{ fontSize: 32 }} />,
    title: 'Privacy Focused',
    description: 'Your data is secure. We process everything in real-time and never store your text.',
  },
  {
    icon: <AutoAwesome sx={{ fontSize: 32 }} />,
    title: 'AI-Powered',
    description: 'Leverage state-of-the-art AI models for accurate and reliable analysis.',
  },
  {
    icon: <CheckCircle sx={{ fontSize: 32 }} />,
    title: 'Always Free',
    description: 'All our tools are completely free to use. No hidden costs or subscriptions.',
  },
];

const stats = [
  { value: '15', label: 'AI Tools' },
  { value: '10K+', label: 'Users' },
  { value: '99.9%', label: 'Uptime' },
  { value: '24/7', label: 'Available' },
];

const getToolOfTheWeek = () => {
  const now = new Date();
  const oneJan = new Date(now.getFullYear(), 0, 1);
  const numberOfDays = Math.floor((now - oneJan) / (24 * 60 * 60 * 1000));
  const weekNumber = Math.ceil((now.getDay() + 1 + numberOfDays) / 7);
  return tools[weekNumber % tools.length];
};

const HomePage = () => {
  const theme = useTheme();
  const { darkMode } = useCustomTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const toolOfTheWeek = React.useMemo(() => getToolOfTheWeek(), []);

  const handleToolClick = (path) => {
    navigate(path);
  };

  return (
    <>
      <Helmet>
        <title>TextSimpler - AI-Powered Text Analysis Tools</title>
        <meta name="description" content="Transform your text with AI-powered tools for sentiment analysis, hashtag generation, emoji suggestions, and more. Professional, fast, and secure." />
        <meta name="keywords" content="text analysis, sentiment analysis, hashtag generator, emoji suggester, AI text detector, readability checker, post splitter" />
      </Helmet>

      {/* Hero Section - Completely Redesigned */}
      <Box
        sx={{
          minHeight: '85vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          background: darkMode
            ? 'radial-gradient(circle at 20% 50%, rgba(99, 102, 241, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(6, 182, 212, 0.1) 0%, transparent 50%)'
            : 'radial-gradient(circle at 20% 50%, rgba(99, 102, 241, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(6, 182, 212, 0.05) 0%, transparent 50%)',
        }}
      >
        {/* Animated Background Shapes */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            left: '5%',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`,
            animation: 'float 6s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': { transform: 'translate(0, 0)' },
              '50%': { transform: 'translate(30px, 30px)' },
            },
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '10%',
            right: '5%',
            width: '400px',
            height: '400px',
            borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.1)} 0%, transparent 70%)`,
            animation: 'float 8s ease-in-out infinite',
            animationDelay: '1s',
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: 8 }}>
          <Box sx={{ textAlign: 'center', maxWidth: 900, mx: 'auto' }}>
            <Box sx={{ mb: 3 }}>
              <Chip
                label="✨ Powered by AI"
                sx={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  px: 1,
                }}
              />
            </Box>
            <Typography
              variant={isMobile ? 'h2' : 'h1'}
              component="h1"
              sx={{
                fontWeight: 800,
                mb: 3,
                fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem' },
                lineHeight: 1.1,
                letterSpacing: '-0.02em',
              }}
            >
              Transform Your Text with{' '}
              <Box
                component="span"
                sx={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                AI Power
              </Box>
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                mb: 4,
                lineHeight: 1.7,
                fontSize: { xs: '1.1rem', md: '1.25rem' },
                fontWeight: 400,
                maxWidth: 700,
                mx: 'auto',
              }}
            >
              Unlock the full potential of your content with our suite of 15 AI-powered tools.
              AI flashcards, quote cards, SEO optimization, and much more - all in one place.
            </Typography>

            {/* Stats */}
            <Grid container spacing={3} sx={{ mb: 5, justifyContent: 'center', maxWidth: 600, mx: 'auto' }}>
              {stats.map((stat, index) => (
                <Grid item xs={6} sm={3} key={index}>
                  <Box>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        background: 'linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ justifyContent: 'center' }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/SentimentAnalyzer')}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)',
                  boxShadow: `0 8px 24px ${alpha('#6366f1', 0.3)}`,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #4f46e5 0%, #0891b2 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: `0 12px 28px ${alpha('#6366f1', 0.4)}`,
                  },
                  transition: 'all 0.3s ease',
                }}
                endIcon={<ArrowForward />}
              >
                Get Started Free
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => document.getElementById('tools').scrollIntoView({ behavior: 'smooth' })}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderWidth: 2,
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  '&:hover': {
                    borderWidth: 2,
                    borderColor: 'primary.dark',
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Explore Tools
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Tool of the Week Section */}
      <Container maxWidth="lg" sx={{ mt: -10, mb: 10, position: 'relative', zIndex: 2 }}>
        <Fade in={true} timeout={1000}>
          <Card
            elevation={0}
            sx={{
              background: darkMode
                ? 'rgba(30, 41, 59, 0.7)'
                : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(20px)',
              borderRadius: 6,
              border: `1px solid ${alpha(toolOfTheWeek.color, 0.3)}`,
              overflow: 'hidden',
              boxShadow: `0 20px 50px ${alpha(toolOfTheWeek.color, 0.15)}`,
              transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
              '&:hover': {
                transform: 'translateY(-10px)',
                boxShadow: `0 30px 60px ${alpha(toolOfTheWeek.color, 0.25)}`,
                borderColor: toolOfTheWeek.color,
              },
            }}
          >
            <Grid container>
              <Grid item xs={12} md={7} sx={{ p: { xs: 4, md: 6 } }}>
                <Stack spacing={3}>
                  <Box>
                    <Chip
                      label="✨ Tool of the Week"
                      size="small"
                      sx={{
                        background: toolOfTheWeek.gradient,
                        color: 'white',
                        fontWeight: 700,
                        px: 1,
                        mb: 2,
                      }}
                    />
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: '2rem', md: '2.5rem' },
                        mb: 2,
                        letterSpacing: '-0.02em',
                      }}
                    >
                      {toolOfTheWeek.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'text.secondary',
                        fontSize: '1.1rem',
                        lineHeight: 1.7,
                        maxWidth: 500,
                      }}
                    >
                      {toolOfTheWeek.description}
                    </Typography>
                  </Box>
                  <Box>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => navigate(toolOfTheWeek.path)}
                      sx={{
                        background: toolOfTheWeek.gradient,
                        px: 4,
                        py: 1.5,
                        borderRadius: 3,
                        fontWeight: 700,
                        fontSize: '1rem',
                        boxShadow: `0 10px 20px ${alpha(toolOfTheWeek.color, 0.3)}`,
                        '&:hover': {
                          opacity: 0.9,
                          transform: 'scale(1.02)',
                        },
                      }}
                      endIcon={<ArrowForward />}
                    >
                      Try Featured Tool
                    </Button>
                  </Box>
                </Stack>
              </Grid>
              <Grid
                item
                xs={12}
                md={5}
                sx={{
                  background: toolOfTheWeek.gradient,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 6,
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {/* Decorative Elements */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '-20%',
                    right: '-20%',
                    width: '100%',
                    height: '100%',
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: '50%',
                  }}
                />
                <Box
                  sx={{
                    zIndex: 1,
                    transform: 'scale(2.5) rotate(-10deg)',
                    color: 'white',
                    filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.2))',
                    animation: 'floatIcon 4s ease-in-out infinite',
                    '@keyframes floatIcon': {
                      '0%, 100%': { transform: 'scale(2.5) rotate(-10deg) translateY(0)' },
                      '50%': { transform: 'scale(2.5) rotate(-10deg) translateY(-10px)' },
                    },
                  }}
                >
                  {toolOfTheWeek.icon}
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Fade>
      </Container>

      {/* Features Section */}
      <Box sx={{ py: 10, backgroundColor: 'background.default' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: '2rem', md: '3rem' },
              }}
            >
              Why Choose TextSimpler?
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
              Built for speed, privacy, and accuracy. Everything you need to analyze and improve your text.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    textAlign: 'center',
                    p: 3,
                    border: `1px solid ${theme.palette.divider}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 64,
                      height: 64,
                      mx: 'auto',
                      mb: 2,
                      background: 'linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)',
                    }}
                  >
                    {feature.icon}
                  </Avatar>
                  <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {feature.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Tools Section */}
      <Box id="tools" sx={{ py: 10, backgroundColor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: '2rem', md: '3rem' },
              }}
            >
              Our AI-Powered Tools
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
              Choose from our comprehensive suite of text analysis tools. Each one designed to make your content better.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {tools.map((tool, index) => (
              <Grid item xs={12} sm={6} lg={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    border: `1px solid ${theme.palette.divider}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 12px 40px ${alpha(tool.color, 0.2)}`,
                      borderColor: tool.color,
                      '& .tool-icon': {
                        transform: 'scale(1.1) rotate(5deg)',
                      },
                    },
                  }}
                  onClick={() => handleToolClick(tool.path)}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: '150px',
                      height: '150px',
                      background: tool.gradient,
                      opacity: 0.1,
                      borderRadius: '0 0 0 100%',
                    }}
                  />
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      className="tool-icon"
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: 2,
                        background: tool.gradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        mb: 2,
                        transition: 'transform 0.3s ease',
                      }}
                    >
                      {tool.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
                      {tool.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, mb: 2 }}>
                      {tool.description}
                    </Typography>
                    <Button
                      endIcon={<ArrowForward />}
                      sx={{
                        color: tool.color,
                        fontWeight: 600,
                        p: 0,
                        '&:hover': {
                          backgroundColor: 'transparent',
                          '& .MuiButton-endIcon': {
                            transform: 'translateX(4px)',
                          },
                        },
                        '& .MuiButton-endIcon': {
                          transition: 'transform 0.3s ease',
                        },
                      }}
                    >
                      Try Now
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: 10,
          background: darkMode
            ? 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'
            : 'linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `radial-gradient(circle at 20% 80%, ${alpha('#fff', 0.1)} 0%, transparent 50%)`,
          }}
        />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <TrendingUp sx={{ fontSize: 60, mb: 3, opacity: 0.9 }} />
          <Typography
            variant="h2"
            sx={{
              mb: 3,
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '3rem' },
            }}
          >
            Ready to Transform Your Text?
          </Typography>
          <Typography
            variant="h6"
            sx={{ mb: 5, opacity: 0.95, lineHeight: 1.7 }}
          >
            Join thousands of content creators, marketers, and professionals who use TextSimpler
            to create better content. Start analyzing your text today - completely free!
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/SentimentAnalyzer')}
            sx={{
              px: 6,
              py: 2,
              fontSize: '1.2rem',
              fontWeight: 600,
              backgroundColor: 'white',
              color: darkMode ? '#1e293b' : '#6366f1',
              boxShadow: `0 8px 32px ${alpha('#000', 0.3)}`,
              '&:hover': {
                backgroundColor: alpha('#fff', 0.95),
                transform: 'translateY(-2px)',
                boxShadow: `0 12px 40px ${alpha('#000', 0.4)}`,
              },
              transition: 'all 0.3s ease',
            }}
            endIcon={<Rocket />}
          >
            Start Analyzing Now
          </Button>
        </Container>
      </Box>
    </>
  );
};

export default HomePage;
